import {
  getObserver,
  createSlider,
  layoutRecalcManager,
  layoutPhaseMeasure,
  layoutPhaseApply,
} from '@theme/theme-global';

class ShopTheLookBanner extends HTMLDivElement {
  constructor() {
    super();
    this.section = this.closest('.shopify-section');
    this.sliderInstance = null;
    this.thumbsInstance = null;
    this._observer = null;
    this._hotspotsSetup = false;
    this._lastWidth = null;
    this.recalculateLayout = this.recalculateLayout.bind(this);
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    layoutRecalcManager.unregister(this.recalculateLayout);
    if (this.sliderInstance?.destroy) {
      this.sliderInstance.destroy(true, true);
    }
    if (this.thumbsInstance?.destroy) {
      this.thumbsInstance.destroy(true, true);
    }
    if (this._observer) {
      this._observer.unobserve(this);
      this._observer = null;
    }
    this.sliderInstance = null;
    this.thumbsInstance = null;
    this._hotspotsSetup = false;
    this._lastWidth = null;
  }

  init() {
    const isFirstSection = this.section?.querySelector('[data-index]')?.dataset.index === '1';
    const thumbsEl = this.section?.querySelector('[is="shop-the-look-thumbs"]');

    const setupSliders = async () => {
      if (this._sliderInitializing) return;
      this._sliderInitializing = true;
      try {
        if (!this.sliderInstance) {
          this.sliderInstance = await createSlider(this, JSON.parse(this.dataset.params));
        }
        if (!this.thumbsInstance && thumbsEl) {
          this.thumbsInstance = await createSlider(thumbsEl, JSON.parse(thumbsEl.dataset.params));
        }
        this.linkControllers();
        this.setupHotspotSync();
        layoutRecalcManager.register(this.recalculateLayout, { root: this });
        this.refreshSizesAfterLayout();
      } finally {
        this._sliderInitializing = false;
      }
    };

    if (isFirstSection) {
      setupSliders();
    } else if ('IntersectionObserver' in window) {
      this._observer = getObserver({ rootMargin: '300px 0px', threshold: 0 }, true, false);
      this._observer.observe(this, setupSliders);
    } else {
      setupSliders();
    }
  }

  async refreshSizesAfterLayout() {
    if (document.fonts?.ready) {
      try {
        await document.fonts.ready;
      } catch (_) {}
    }

    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    if (!this.isConnected) return;

    this._lastWidth = null;
    this.updateSliderSizes(this.measureNavWidth());
  }

  measureNavWidth() {
    const thumbsEl = this.thumbsInstance?.el;
    if (thumbsEl?.clientWidth) return thumbsEl.clientWidth;
    return this.clientWidth || 0;
  }

  updateSliderSizes(width) {
    if (!width || width === this._lastWidth) return;
    this._lastWidth = width;

    if (this.sliderInstance && !this.sliderInstance.destroyed) {
      this.sliderInstance.update();
    }
    if (this.thumbsInstance && !this.thumbsInstance.destroyed) {
      this.thumbsInstance.update();
    }
  }

  recalculateLayout(phase) {
    if (layoutPhaseMeasure(phase)) {
      this._pendingWidth = this.measureNavWidth();
    }

    if (layoutPhaseApply(phase)) {
      this.updateSliderSizes(this._pendingWidth ?? this.measureNavWidth());
    }
  }

  linkControllers() {
    if (!this.sliderInstance || !this.thumbsInstance) return;

    this.sliderInstance.controller.control = this.thumbsInstance;
    this.thumbsInstance.controller.control = this.sliderInstance;
  }

  setupHotspotSync() {
    if (!this.sliderInstance || this._hotspotsSetup) return;
    this._hotspotsSetup = true;

    this.section.querySelectorAll('[data-hotspot="true"]').forEach((hotspot) => {
      hotspot.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const index = Number(hotspot.dataset.index);
        if (this.sliderInstance.params.loop) {
          this.sliderInstance.slideToLoop(index);
        } else {
          this.sliderInstance.slideTo(index);
        }
      });
    });

    this.sliderInstance.on('slideChange', () => {
      this.setActiveHotspot(this.sliderInstance.realIndex);
    });

    this.setActiveHotspot(this.sliderInstance.realIndex);
  }

  setActiveHotspot(index) {
    this.section.querySelectorAll('[data-hotspot="true"]').forEach((hotspot) => {
      hotspot.classList.toggle('active', Number(hotspot.dataset.index) === index);
    });
  }
}

if (!customElements.get('shop-the-look-banner')) {
  customElements.define('shop-the-look-banner', ShopTheLookBanner, { extends: 'div' });
}
